/**
 * Leave-request submit schema (D-10 + Pitfall H7).
 *
 * **Why Zod here, not on the backend:** per D-10, the frontend is the primary
 * validator for attachment-required + date-range correctness; the backend's
 * RFC 7807 `LEAVE_ATTACHMENT_REQUIRED` 422 is defense-in-depth for the rare
 * cases where attachment state changes between the initial GET of the leave
 * type catalog and the POST of the submit.
 *
 * **Discriminator:** `leaveTypeCode`. Enumerates the 8 Jordan defaults that be-011
 * auto-seeds (ANNUAL, SICK, MATERNITY, PATERNITY, HAJJ, BEREAVEMENT, UNPAID,
 * CASUAL — matching the Task 3 `leaves.type.*` i18n bundle). Tenant-added codes
 * (unknown at compile time) fall through to `fallbackLeaveRequestSchema`.
 *
 * **Cross-field refinement:** `endDate >= startDate` is enforced by
 * `refineLeaveDateRange` — exported separately so it composes with RHF's
 * `zodResolver` when callers wrap it via `.superRefine`.
 *
 * **Attachment-required check:** the leave-type catalog entry carries
 * `attachmentRequired: boolean`. The consumer resolves that flag at form-mount
 * time (from `useLeaveTypes`) and wraps the schema via `withAttachmentRequired`.
 * This is split out instead of baked into each variant so the check can be
 * toggled per leave type at runtime without re-compiling the schema.
 */
import { z } from "zod";

/** Shared sub-schema — attaches to every type variant below. */
const baseLeaveRequestShape = {
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date-format"),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "date-format"),
  reason: z.string().max(500).optional(),
  attachmentFileKey: z.string().optional(),
  /**
   * CC-63 (be-058) — optional half-day flag. UI gates visibility to
   * single-day ranges; backend's `LEAVE_HALF_DAY_REQUIRES_SINGLE_DAY` 400
   * is the authoritative validator if start !== end.
   */
  halfDay: z.boolean().optional(),
  /**
   * Pay the vacation salary in advance (annual leave). Without it the
   * resolver's parse strips the employee's choice before the submit body.
   */
  vacationInAdvanceSalary: z.boolean().optional(),
  /**
   * Leave by the hour: HH:mm on `startDate` (= `endDate`), end exclusive. The
   * server places them inside that date's schedule and returns the day
   * offsets, so none are sent. Without these the parse strips them.
   */
  fromTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
  toTime: z
    .string()
    .regex(/^\d{2}:\d{2}$/)
    .optional(),
} as const;

/**
 * Discriminated union over the 8 Jordan-seeded codes (matches i18n
 * `leaves.type.*`). Unknown codes fall through to `fallbackLeaveRequestSchema`.
 * Pitfall H7: PATERNITY + CASUAL are the 4th and 8th entries respectively — NOT
 * STUDY (which is not seeded).
 */
export const knownLeaveRequestSchema = z.discriminatedUnion("leaveTypeCode", [
  z.object({ leaveTypeCode: z.literal("ANNUAL"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("SICK"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("MATERNITY"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("PATERNITY"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("HAJJ"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("BEREAVEMENT"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("UNPAID"), ...baseLeaveRequestShape }),
  z.object({ leaveTypeCode: z.literal("CASUAL"), ...baseLeaveRequestShape }),
]);

/** Fallback for tenant-added types (codes unknown at compile time). */
export const fallbackLeaveRequestSchema = z.object({
  leaveTypeCode: z.string().min(1),
  ...baseLeaveRequestShape,
});

/**
 * Public schema: tries the discriminated-union first (faster + better error
 * narrowing), falls back to the permissive object for tenant-added codes.
 */
export const leaveRequestSchema = z.union([knownLeaveRequestSchema, fallbackLeaveRequestSchema]);

/**
 * Cross-field refinement — `endDate >= startDate`. ISO-8601 date strings
 * compare lexicographically, so direct string comparison is correct for YYYY-MM-DD.
 * Export as a standalone refinement so callers can chain with `.superRefine`
 * around the base schema without rebuilding the discriminated union.
 */
export function refineLeaveDateRange<T extends { startDate: string; endDate: string }>(
  v: T,
  ctx: z.RefinementCtx,
): void {
  if (v.endDate < v.startDate) {
    ctx.addIssue({
      code: "custom",
      message: "end-before-start",
      path: ["endDate"],
    });
  }
}

/**
 * Builder — returns a superRefine-compatible fn that, given the runtime
 * leave-type catalog entry's `attachmentRequired` flag, enforces
 * `attachmentFileKey` presence. When `required === false` (the common case),
 * the returned fn is a no-op.
 *
 * Usage (inside RHF zodResolver):
 *   const required = leaveType?.attachmentRequired ?? false;
 *   const schema = leaveRequestSchema.superRefine(withAttachmentRequired(required));
 */
export function withAttachmentRequired<T extends { attachmentFileKey?: string }>(
  required: boolean,
): (v: T, ctx: z.RefinementCtx) => void {
  return (v, ctx) => {
    if (required && !v.attachmentFileKey) {
      ctx.addIssue({
        code: "custom",
        message: "attachment-required",
        path: ["attachmentFileKey"],
      });
    }
  };
}

/** Inferred type — discriminated-union preserves per-code narrowing on consumers. */
export type LeaveRequestFormValues = z.infer<typeof leaveRequestSchema>;
